import { useEffect, useMemo, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { toast } from 'sonner'
import { z } from 'zod/v3'
import { ArrowLeft, Save } from 'lucide-react'

import type { LotOption } from '@/components/lots/lot-items-field'
import type { CreateOutNoteRequest } from '@/lib/api/packaging-lot-api'
import { StickyFormFooter } from '@/components/common/StickyFormFooter'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import { LotItemsField } from '@/components/lots/lot-items-field'
import { Combobox } from '@/components/ui/combobox'
import { packagingLotApi } from '@/lib/api/packaging-lot-api'
import { useSalesOrders } from '@/hooks/use-sales-order-query'
import {
    ScanLotCodeButton,
    makeLotIdParser,
} from '@/components/lots/scan-lot-code-button'
import {
    useCreatePackagingOutNote,
    usePackagingLots,
} from '@/hooks/use-packaging-lot-query'
import { useDebounced } from '@/hooks/use-debounced'
import { packagingLotOption } from '@/components/lots/lot-option-builders'
import { m } from '@/lib/paraglide/messages'

const itemSchema = z.object({
    kind: z.literal('existing'),
    packaging_lot_id: z
        .string({ required_error: m.form_err_pick_existing_lot() })
        .uuid(m.form_err_pick_existing_lot()),
    quantity: z.coerce
        .number({
            required_error: m.form_err_quantity_required(),
            invalid_type_error: m.form_err_number_invalid(),
        })
        .positive(m.form_err_quantity_positive()),
})

const schema = z
    .object({
        reason: z.enum([
            'for_order',
            'discard',
            'adjustment',
            'for_packaging_process',
        ]),
        order_id: z.string().uuid().optional().or(z.literal('')),
        notes: z.string().max(2000, m.form_err_notes_too_long()).optional(),
        items: z.array(itemSchema).min(1, m.form_err_items_min_one()),
    })
    .superRefine((val, ctx) => {
        // Reason=for_order bắt buộc phải chọn đơn — BE cũng enforce, guard ở
        // đây để user thấy lỗi ngay không phải chờ round-trip.
        if (val.reason === 'for_order' && !val.order_id) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['order_id'],
                message: m.pkl_out_order_required(),
            })
        }
    })

type FormData = z.infer<typeof schema>
type OrderOpt = { value: string; label: string }

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/packaging-warehouse/stock-out-notes/new',
)({
    component: NewPXKPage,
})

function NewPXKPage() {
    const navigate = useNavigate()
    const create = useCreatePackagingOutNote()

    const handleSubmit = async (value: CreateOutNoteRequest) => {
        try {
            const res = await create.mutateAsync(value)
            const noteId = res.data?.id
            if (noteId) {
                void navigate({
                    to: '/packaging-warehouse/stock-out-notes/$id',
                    params: { id: noteId },
                })
            }
        } catch (err) {
            toast.error(
                err instanceof Error ? err.message : m.sl_new_error_create(),
            )
        }
    }

    return (
        <PackagingLotStockOutForm
            isPending={create.isPending}
            onSubmit={(v) => void handleSubmit(v)}
            onCancel={() =>
                navigate({
                    to: '/packaging-warehouse/stock-out-notes',
                    search: { page: 1 },
                })
            }
        />
    )
}

export function PackagingLotStockOutForm({
    mode = 'create',
    initialValues,
    title,
    submitLabel,
    seedOrderOptions,
    isPending,
    onSubmit,
    onCancel,
}: {
    mode?: 'create' | 'edit'
    initialValues?: FormData
    title?: string
    submitLabel?: string
    /** Seed đơn hàng hiện tại (khi sửa phiếu for_order) để combobox hiển thị. */
    seedOrderOptions?: OrderOpt[]
    isPending: boolean
    onSubmit: (value: CreateOutNoteRequest) => void
    onCancel: () => void
}) {
    const isEdit = mode === 'edit'
    const [lotSearch, setLotSearch] = useState('')
    const debouncedSearch = useDebounced(lotSearch)
    const { data: lotsData } = usePackagingLots({
        per_page: 50,
        only_available: '1',
        search: debouncedSearch || undefined,
    })

    const form = useForm<FormData>({
        resolver: zodResolver(schema),
        defaultValues: initialValues ?? {
            reason: 'for_order',
            order_id: '',
            notes: '',
            items: [{ kind: 'existing', packaging_lot_id: '', quantity: 0 }],
        },
    })

    const reason = form.watch('reason')
    const isForOrder = reason === 'for_order'

    // Reset order_id khi user đổi sang reason khác — tránh gửi order_id ngoài
    // ngữ cảnh. Khi SỬA, reason bị khoá nên không reset (giữ đơn của phiếu gốc).
    useEffect(() => {
        if (!isForOrder && !isEdit) form.setValue('order_id', '')
    }, [isForOrder, isEdit, form])

    const [orderSearch, setOrderSearch] = useState('')
    const debouncedOrderSearch = useDebounced(orderSearch)
    const { data: ordersData } = useSalesOrders(
        isForOrder
            ? {
                  per_page: 30,
                  ...(debouncedOrderSearch
                      ? { search: debouncedOrderSearch }
                      : {}),
              }
            : undefined,
    )
    // Đơn hàng đã chọn tay được cache để nhãn không rớt khỏi trigger khi search
    // server đổi options (server chỉ trả danh sách theo từ khoá hiện tại;
    // seedOrderOptions chỉ có đơn gốc của phiếu khi sửa).
    const [pickedOrders, setPickedOrders] = useState<Map<string, OrderOpt>>(
        new Map(),
    )
    const orderOptions = useMemo<OrderOpt[]>(() => {
        const map = new Map<string, OrderOpt>()
        for (const o of seedOrderOptions ?? []) map.set(o.value, o)
        for (const o of ordersData?.data ?? []) {
            map.set(o.id, {
                value: o.id,
                label: `${o.order_code} — ${o.customer_name}`,
            })
        }
        for (const [id, o] of pickedOrders) map.set(id, o)
        return Array.from(map.values())
    }, [ordersData, seedOrderOptions, pickedOrders])

    const lotOptions = useMemo(
        () => (lotsData?.data ?? []).map(packagingLotOption),
        [lotsData],
    )

    // Cache lot đã quét mã vạch — usePackagingLots top 50, lô scan có thể
    // ngoài trang đầu → Combobox không hiện tên.
    const [scannedLots, setScannedLots] = useState<Map<string, LotOption>>(
        new Map(),
    )
    const injectedScannedLots = useMemo(
        () => Array.from(scannedLots.values()),
        [scannedLots],
    )
    const fetchAndCacheLot = async (lotId: string) => {
        if (scannedLots.has(lotId)) return
        try {
            const res = await packagingLotApi.getLot(lotId)
            const l = res.data
            if (!l) return
            setScannedLots((prev) => {
                if (prev.has(lotId)) return prev
                const next = new Map(prev)
                next.set(lotId, packagingLotOption(l))
                return next
            })
        } catch {
            // ignore
        }
    }

    const submit = form.handleSubmit((data) => {
        const items = data.items.map((it) => ({
            packaging_lot_id: it.packaging_lot_id,
            quantity: Number(it.quantity),
        }))
        onSubmit({
            reason: data.reason,
            notes: data.notes,
            items,
            ...(data.reason === 'for_order' && data.order_id
                ? { order_id: data.order_id }
                : {}),
        })
    })

    return (
        <div className="space-y-4 p-4 pb-24 md:p-6 md:pb-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="sm" onClick={onCancel}>
                        <ArrowLeft className="size-4 mr-1" /> {m.sl_lot_back()}
                    </Button>
                    <h1 className="text-xl font-semibold">
                        {title ?? m.pkl_new_pxk_title()}
                    </h1>
                </div>
            </div>

            <FormProvider {...form}>
                <form onSubmit={submit} className="space-y-6 max-w-4xl">
                    <div className="grid gap-4 md:grid-cols-2 rounded-md border bg-card p-4">
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_reason_out()}
                            </Label>
                            <Controller
                                control={form.control}
                                name="reason"
                                render={({ field }) => (
                                    <Select
                                        value={field.value}
                                        disabled={isEdit}
                                        onValueChange={field.onChange}
                                    >
                                        <SelectTrigger className="w-full">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="for_order">
                                                {m.pkl_out_reason_for_sale()}
                                            </SelectItem>
                                            <SelectItem value="discard">
                                                {m.pkl_out_reason_discard()}
                                            </SelectItem>
                                            <SelectItem value="adjustment">
                                                {m.sl_new_reason_out_adjustment()}
                                            </SelectItem>
                                            {/* PXK sang quy trình đóng gói giờ được hệ
                                                thống tự sinh khi duyệt PNK thành phẩm có
                                                khai vật tư — chỉ hiện khi xem/sửa phiếu
                                                cũ còn mang lý do này. */}
                                            {form.watch('reason') ===
                                                'for_packaging_process' && (
                                                <SelectItem value="for_packaging_process">
                                                    {m.pxbb_v2_reason_for_packaging_process()}
                                                </SelectItem>
                                            )}
                                        </SelectContent>
                                    </Select>
                                )}
                            />
                        </div>
                        <div>
                            <Label className="mb-1 block">
                                {m.sl_new_notes()}
                            </Label>
                            <Input
                                {...form.register('notes')}
                                placeholder={m.sl_new_notes_placeholder()}
                            />
                        </div>
                        {isForOrder && (
                            <div className="md:col-span-2">
                                <Label className="mb-1 block">
                                    {m.pkl_out_order_label()}
                                </Label>
                                <Controller
                                    control={form.control}
                                    name="order_id"
                                    render={({ field }) => (
                                        <Combobox
                                            options={orderOptions}
                                            value={field.value || undefined}
                                            onChange={(v) => {
                                                const opt = orderOptions.find(
                                                    (o) => o.value === v,
                                                )
                                                if (opt)
                                                    setPickedOrders((prev) => {
                                                        if (prev.has(v))
                                                            return prev
                                                        const next = new Map(
                                                            prev,
                                                        )
                                                        next.set(v, opt)
                                                        return next
                                                    })
                                                field.onChange(v)
                                            }}
                                            onSearchChange={setOrderSearch}
                                            onOpenChange={(open) => {
                                                if (!open) setOrderSearch('')
                                            }}
                                            disabled={isEdit}
                                            shouldFilter={false}
                                            placeholder={m.pkl_out_order_placeholder()}
                                            searchPlaceholder={m.pkl_out_order_search_placeholder()}
                                            emptyText={m.pkl_out_order_empty()}
                                        />
                                    )}
                                />
                                {form.formState.errors.order_id?.message && (
                                    <p className="text-sm text-destructive mt-1">
                                        {form.formState.errors.order_id.message}
                                    </p>
                                )}
                            </div>
                        )}
                    </div>

                    <LotItemsField
                        mode="out"
                        lotOptions={lotOptions}
                        scannedLots={injectedScannedLots}
                        onLotSearchChange={setLotSearch}
                        categoryOptions={[]}
                        categoryLabel={m.sl_lot_grade()}
                        existingIdField="packaging_lot_id"
                        headerAction={
                            <ScanLotCodeButton
                                parseLotId={makeLotIdParser(
                                    'packaging-warehouse/lots',
                                )}
                                onScanned={(lotId) => {
                                    const current =
                                        form.getValues('items') ?? []
                                    form.setValue(
                                        'items',
                                        [
                                            ...current,
                                            {
                                                kind: 'existing',
                                                packaging_lot_id: lotId,
                                                quantity: 0,
                                            },
                                        ],
                                        {
                                            shouldDirty: true,
                                            shouldValidate: false,
                                        },
                                    )
                                    void fetchAndCacheLot(lotId)
                                }}
                            />
                        }
                    />

                    {form.formState.errors.items?.message && (
                        <p className="text-sm text-destructive">
                            {form.formState.errors.items.message}
                        </p>
                    )}

                    <StickyFormFooter>
                        <Button
                            type="button"
                            variant="outline"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            onClick={onCancel}
                        >
                            {m.sl_new_cancel()}
                        </Button>
                        <Button
                            type="submit"
                            className="h-11 flex-1 md:h-10 md:flex-none"
                            disabled={isPending}
                        >
                            <Save className="size-4 mr-1" />
                            {submitLabel ??
                                (isPending
                                    ? m.sl_new_submitting()
                                    : m.sl_new_submit_pnk())}
                        </Button>
                    </StickyFormFooter>
                </form>
            </FormProvider>
        </div>
    )
}
